/**
 * ColumnMapperModal
 * =================
 * Modal to let users manually map CSV columns to required fields
 * when automatic detection fails.
 */

import React, { useState } from 'react';

const ColumnMapperModal = ({ 
  missingFields, 
  availableColumns, 
  onConfirm, 
  onCancel 
}) => {
  const [mappings, setMappings] = useState({});

  const handleColumnSelect = (canonicalField, sourceColumn) => {
    setMappings(prev => ({
      ...prev,
      [canonicalField]: sourceColumn
    }));
  };

  const handleConfirm = () => {
    // Check all required fields are mapped
    const allMapped = missingFields
      .filter(f => f.required)
      .every(f => mappings[f.field]);
    
    if (!allMapped) {
      alert('Please map all required fields before continuing.');
      return;
    }
    
    onConfirm(mappings);
  };

  // Filter out columns that look like they might be the target
  const getSuggestedColumns = (field) => {
    const fieldLower = field.toLowerCase();
    return availableColumns.filter(col => {
      const colLower = col.toLowerCase();
      // Suggest columns containing relevant keywords
      if (fieldLower.includes('fis') && (colLower.includes('fis') || colLower.includes('license') || colLower.includes('licence'))) {
        return true;
      }
      if (fieldLower.includes('ussa') && (colLower.includes('ussa') || colLower.includes('nc') || colLower.includes('license') || colLower.includes('licence'))) {
        return true;
      }
      return false;
    });
  };

  return (
    <div className="modal-overlay">
      <div className="column-mapper-modal">
        <div className="modal-header">
          <h2>⚠️ Missing Required Fields</h2>
          <p>
            The uploaded CSV doesn't contain recognizable columns for some required fields.
            Please select which column contains each field:
          </p>
        </div>

        <div className="modal-body">
          {missingFields.map(({ field, label, required }) => {
            const suggested = getSuggestedColumns(field);
            return (
              <div key={field} className="field-mapping-row">
                <div className="field-label">
                  <strong>{label}</strong>
                  {required && <span className="required-badge">Required</span>}
                </div>
                <div className="field-select">
                  <select
                    value={mappings[field] || ''}
                    onChange={(e) => handleColumnSelect(field, e.target.value)}
                    className={mappings[field] ? 'mapped' : 'unmapped'}
                  >
                    <option value="">-- Select a column --</option>
                    {suggested.length > 0 && (
                      <optgroup label="Suggested">
                        {suggested.map(col => (
                          <option key={col} value={col}>
                            {col}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    <optgroup label="All Columns">
                      {availableColumns.map(col => (
                        <option key={col} value={col}>
                          {col}
                        </option>
                      ))}
                    </optgroup>
                  </select>
                </div>
              </div>
            );
          })}
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onCancel}>
            Cancel
          </button>
          <button className="btn-primary" onClick={handleConfirm}>
            Apply Mapping
          </button>
        </div>
      </div>

      <style>{`
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.6);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .column-mapper-modal {
          background: #fff;
          border-radius: 12px;
          max-width: 520px;
          width: 90%;
          max-height: 80vh;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        }

        .modal-header {
          padding: 24px;
          background: linear-gradient(135deg, #ff6b35 0%, #e63946 100%);
          color: white;
        }

        .modal-header h2 {
          margin: 0 0 8px 0;
          font-size: 1.4rem;
        }

        .modal-header p {
          margin: 0;
          opacity: 0.9;
          font-size: 0.95rem;
          line-height: 1.5;
        }

        .modal-body {
          padding: 24px;
          overflow-y: auto;
        }

        .field-mapping-row {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 12px 0;
          border-bottom: 1px solid #eee;
        }

        .field-mapping-row:last-child {
          border-bottom: none;
        }

        .field-label {
          flex: 1;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .required-badge {
          background: #e63946;
          color: white;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 0.7rem;
          font-weight: 600;
          text-transform: uppercase;
        }

        .field-select {
          flex: 1;
        }

        .field-select select {
          width: 100%;
          padding: 10px 12px;
          border: 2px solid #ddd;
          border-radius: 8px;
          font-size: 0.95rem;
          background: white;
          cursor: pointer;
          transition: border-color 0.2s;
        }

        .field-select select:focus {
          outline: none;
          border-color: #3498db;
        }

        .field-select select.mapped {
          border-color: #27ae60;
          background: #f0fff4;
        }

        .field-select select.unmapped {
          border-color: #e74c3c;
        }

        .modal-footer {
          padding: 16px 24px;
          background: #f8f9fa;
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          border-top: 1px solid #eee;
        }

        .modal-footer button {
          padding: 10px 24px;
          border-radius: 8px;
          font-size: 0.95rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-secondary {
          background: white;
          border: 2px solid #ddd;
          color: #666;
        }

        .btn-secondary:hover {
          background: #f5f5f5;
          border-color: #bbb;
        }

        .btn-primary {
          background: linear-gradient(135deg, #3498db 0%, #2980b9 100%);
          border: none;
          color: white;
        }

        .btn-primary:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(52, 152, 219, 0.4);
        }
      `}</style>
    </div>
  );
};

export default ColumnMapperModal;
